import { ApplicationConfig, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withXhr } from '@angular/common/http';
import { provideAnimations, provideNoopAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import { GlobalErrorHandler } from './@commons/@services/front/global-error-handler';
import { ScrollRestorationService } from './@commons/services/scroll-restoration.service';
import { provideAppShellCache } from './@commons/@services/front/app-shell-cache';
import { APP_VERSION } from './@commons/constants/app-version';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Fichiers de traduction, adresse propre à chaque build : sans Cache-Control
 * avant le 27/10/2026, `fr.json` restait des jours dans la WebView d'iOS, et
 * une nouvelle app y cherchait des clés absentes (« home.today.title »).
 * Le service worker les retrouve sans tenir compte du `?v=` (ngsw-config.json).
 */
export const TRANSLATION_FILES = { prefix: './assets/i18n/', suffix: `.json?v=${APP_VERSION}` };

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    // Erreurs non rattrapées : console + PostHog (version, route), cf. GlobalErrorHandler.
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    // En avant, une nouvelle page s'ouvre en haut (« Nouvelle planche » n'hérite
    // pas du défilement de la vue précédente) ; au retour, la page reprend là
    // où on l'avait laissée (NA-04), y compris quand sa liste arrive après.
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideAppInitializer(() => inject(ScrollRestorationService).listen()),
    // Navigateur seulement : l'enveloppe de l'app s'ouvre sans réseau (NA-05).
    ...provideAppShellCache(),
    provideHttpClient(withXhr()),
    // Qui a demandé moins de mouvement au système n'a pas d'animation Angular
    // (fondus, entrées de page) ; les animations CSS suivent styles.scss.
    prefersReducedMotion() ? provideNoopAnimations() : provideAnimations(),
    provideTranslateService({
      loader: provideTranslateHttpLoader(TRANSLATION_FILES),
      fallbackLang: 'fr',
      lang: 'fr'
    }),
  ],
};
